import { theme } from '@/analyses/_shared/theme';
import { COUNTRY_BY_NAME } from '@/data/countries';
import { TIER_CONFIG } from '@/data/tiers';

export type CompareViewProps = {
  countryNames: string[];
  onRemove: (name: string) => void;
  onClose: () => void;
  onOpenAnalysis: (name: string) => void;
};

export const CompareView = ({
  countryNames,
  onRemove,
  onClose,
  onOpenAnalysis,
}: CompareViewProps) => {
  if (countryNames.length === 0) return null;

  const countries = countryNames
    .map((n) => COUNTRY_BY_NAME[n.toLowerCase()])
    .filter(Boolean);

  return (
    <div
      className="no-print"
      style={{
        position: 'fixed',
        left: 0,
        right: 0,
        bottom: 0,
        padding: 16,
        background: theme.DARK2,
        borderTop: `1px solid ${theme.BORDER}`,
        zIndex: 90,
        boxShadow: '0 -8px 24px rgba(0,0,0,0.4)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            color: theme.WHITE,
            fontSize: 13,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          Compare ({countries.length})
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'transparent',
            color: theme.MUTED,
            border: `1px solid ${theme.BORDER}`,
            borderRadius: 6,
            padding: '4px 10px',
            cursor: 'pointer',
            fontSize: 12,
            fontFamily: 'inherit',
          }}
        >
          Close
        </button>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${countries.length}, minmax(220px, 1fr))`,
          gap: 12,
        }}
      >
        {countries.map((c) => {
          const cfg = TIER_CONFIG[c.tier];
          return (
            <div
              key={c.iso2}
              style={{
                background: theme.CARD,
                border: `1px solid ${theme.BORDER}`,
                borderRadius: 10,
                padding: 14,
                position: 'relative',
              }}
            >
              <button
                onClick={() => onRemove(c.name)}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  background: 'transparent',
                  color: theme.MUTED,
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: 14,
                  fontFamily: 'inherit',
                }}
                title="Remove"
              >
                ×
              </button>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  marginBottom: 6,
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: '50%',
                    background: cfg.color,
                  }}
                />
                <span
                  style={{
                    color: cfg.color,
                    fontSize: 10,
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                  }}
                >
                  {cfg.label}
                </span>
              </div>
              <div
                style={{
                  color: theme.WHITE,
                  fontSize: 15,
                  fontWeight: 700,
                  marginBottom: 4,
                }}
              >
                {c.name}
              </div>
              <div
                style={{
                  color: theme.MUTED,
                  fontSize: 12,
                  minHeight: 52,
                  lineHeight: 1.5,
                }}
              >
                {c.summary ?? '—'}
              </div>
              <div
                style={{
                  marginTop: 10,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <span style={{ color: theme.MUTED, fontSize: 11 }}>
                  {c.lastUpdated ?? 'Not dated'}
                </span>
                {c.analysisKey && (
                  <button
                    onClick={() => onOpenAnalysis(c.name)}
                    style={{
                      background: 'transparent',
                      color: theme.BLUE,
                      border: `1px solid ${theme.BLUE}44`,
                      borderRadius: 6,
                      padding: '3px 10px',
                      cursor: 'pointer',
                      fontSize: 11,
                      fontWeight: 600,
                      fontFamily: 'inherit',
                    }}
                  >
                    Open →
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
